/**
 * RouteAnnouncer — Route-change a11y contract.
 *
 * On every location change:
 *   1. Updates document.title from ROUTE_META.
 *   2. Moves focus to <main id="main-content"> (tabIndex={-1}) so keyboard users
 *      don't have to Tab from the browser chrome each navigation.
 *   3. Announces the new page name via a visually-hidden aria-live region so
 *      screen-reader users are told they navigated.
 *
 * Skips the initial mount (no announcement needed — screen readers already read
 * the page on load) but still sets the title.
 *
 * Companion to SkipLink; both target #main-content.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveRoute } from './routes';

export const RouteAnnouncer = () => {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    const resolved = resolveRoute(location.pathname);
    const title = resolved?.meta.title(resolved.params) ?? 'Station Explorer';

    document.title = title;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Strip the trailing " · Station Explorer" from the SR announcement — the
    // app name is redundant every navigation.
    const pageName = title.replace(/\s*·\s*Station Explorer\s*$/i, '');
    setAnnouncement(`Navigated to ${pageName}`);

    // Move focus to <main> after the new page renders. rAF lets React commit first.
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      if (main instanceof HTMLElement) {
        main.focus({ preventScroll: false });
      }
    });
  }, [location.pathname]);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {announcement}
    </div>
  );
};
